import { useCallback, useRef } from 'react';

import { deleteToken } from '../../helpers/auth';
import { unauthenticatedClient } from './../../api/apollo';
import { User } from './Auth';
import { useSignUpMutation } from './__generated__/register.mutation.generated';

export const useRegister = () => {
	const abortControllerRef = useRef<AbortController>();
	const [registerUser] = useSignUpMutation({ client: unauthenticatedClient });

	const handleRegister = useCallback(
		async ({
			email,
			password,
			username,
			newsletter,
			registerCode,
		}: {
			email: string;
			password: string;
			username: string;
			newsletter?: boolean;
			registerCode?: string;
		}): Promise<{
			user: User;
			accessToken: string;
			refreshToken: string;
			mediaToken: string;
		}> => {
			abortControllerRef.current = new AbortController();
			await deleteToken();
			const result = await registerUser({
				variables: { email, password, username, newsletter, registerCode },
			});

			if (result.errors?.length) {
				throw Error(result.errors[0].message);
			}

			if (!result.data) {
				throw Error('No auth data returned');
			}

			return result.data.signup;
		},
		[registerUser],
	);

	const abortRegister: () => void = useCallback(
		() => abortControllerRef.current && abortControllerRef.current.abort(),
		[],
	);

	return { handleRegister, abortRegister };
};
